import { BrowserRouter as Router, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { RouteChangeProvider } from './contexts/RouteChangeContext'
import { ConfigProvider, useConfig } from './contexts/ConfigContext'
import { PageReadyHandler, LoadingFallback, ErrorBoundary } from './ui/components/common'
import ProtectedRoute from './components/auth/ProtectedRoute'
import { getSync } from './contexts/AuthContextHelper'
import { useEffect, Suspense, lazy, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './App.css'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { ModalProvider } from './app/contexts/ModalContext'
import { appConfigService } from './services/appConfig'
import { enablePWA } from './config/pwa-config'

// Components - Using fragment-based header/footer (Phase 3: HTML injection with React hydration)
import { GlobalLoadingIndicator, CookieConsent, ScrollToTop } from './ui/components/common'
import { AppLayout } from './ui/components/layout'
import { AppRoutes } from './ui/routes'



// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Component to handle top-level authentication loading
const AuthenticationLoadingHandler = ({ children }) => {
  const { loading, tokenRefreshInProgress } = useAuth();

  return (
    <>
      <GlobalLoadingIndicator
        isVisible={loading || tokenRefreshInProgress}
        message={tokenRefreshInProgress ? "Refreshing your session..." : "Loading your account..."}
      />
      {children}
    </>
  );
};


function App() {
  const [serviceWorkerRegistered, setServiceWorkerRegistered] = useState(false);
  const [tokenRefreshed, setTokenRefreshed] = useState(false);

  // Handle token refresh event
  useEffect(() => {
    const handleTokenRefreshEnd = (event) => {
      if (event.detail?.success) {
        console.log('[App] Token refresh completed successfully');
        setTokenRefreshed(true);

        // Allow a small delay for auth state to propagate
        setTimeout(() => {
          getSync().then(({ syncCoordinator, syncService }) => {
            if (!syncCoordinator && !syncService) return;
            if (syncService?.triggerPostAuthSync) {
              syncService.triggerPostAuthSync('post_token_refresh_app').catch(error => {
                console.error('[App] Failed to trigger post-auth sync after token refresh:', error);
              });
            } else if (syncCoordinator?.triggerSync) {
              syncCoordinator.triggerSync('post_token_refresh_app').catch(error => {
                console.error('[App] Failed to trigger sync after token refresh:', error);
              });
            }
          }).catch(error => {
            console.error('[App] Error loading sync:', error);
          });
        }, 500);
      }
    };

    window.addEventListener('token-refresh-end', handleTokenRefreshEnd);

    return () => {
      window.removeEventListener('token-refresh-end', handleTokenRefreshEnd);
    };
  }, []);

  useEffect(() => {
    // Register service worker (disabled in development)
    const registerServiceWorker = async () => {
      // Disable service worker in development mode (Vite dev server)
      const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';

      // Also disable on localhost:4242 (backend dev server serving built frontend)
      // This prevents constant SW updates during dev when vite is rebuilding
      const isBackendDevServer = window.location.hostname === 'localhost' && window.location.port === '4242';

      if (isDevelopment || isBackendDevServer) {
        // Unregister any existing service workers in development
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            await registration.unregister();
            console.log('Service Worker unregistered (development mode)');
          }
        }
        return;
      }

      // Only register when PWA is enabled (service-worker.js is built); otherwise the URL returns HTML and registration fails
      if (!enablePWA) {
        return;
      }

      if ('serviceWorker' in navigator) {
        try {
          // Register the service worker
          const registration = await navigator.serviceWorker.register('/service-worker.js');
          console.log('Service Worker registered with scope:', registration.scope);

          // Store the registration globally so other components can access it
          window.swRegistration = registration;
          setServiceWorkerRegistered(true);

          // Check if service worker is active
          if (registration.active) {
            // Notify service worker that we're up and ready for any pending syncs
            registration.active.postMessage({
              type: 'CHECK_SYNC_PENDING',
              timestamp: Date.now()
            });
          }

          // Add message event listener to handle service worker responses
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'SYNC_STATUS') {
              console.log('[App] Received sync status from service worker:', event.data);
            }
          });
        } catch (error) {
          console.error('Service Worker registration failed:', error);
        }
      }
    };

    registerServiceWorker();

    getSync().then(({ syncCoordinator }) => {
      if (syncCoordinator) {
        syncCoordinator.initialize({ triggerSyncAfterInit: true }).catch(error => {
          console.error('Failed to initialize sync coordinator:', error);
        });
      }
    });

    return () => {
      getSync().then(({ syncCoordinator }) => {
        if (syncCoordinator) syncCoordinator.destroy();
      });
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', () => { });
      }
    };
  }, []);

  // Helper function for lazy loaded routes
  const LazyRoute = ({ element, requiredRole }) => (
    <ProtectedRoute requiredRole={requiredRole}>
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          {element}
        </Suspense>
      </ErrorBoundary>
    </ProtectedRoute>
  );

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ScrollToTop />
        <RouteChangeProvider transitionTimeout={2000} showDelay={400} minIndicatorDisplay={500}>
          <ConfigProvider>
            <AuthProvider>
              <ModalProvider>
                <NotificationProvider>
                  <AuthenticationLoadingHandler>
                    <PageReadyHandler contentSelector="main" delay={50} />
                    <ToastContainer
                      position="top-right"
                      autoClose={5000}
                      hideProgressBar={false}
                      newestOnTop={false}
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                      theme="colored"
                    />
                    <AppLayout>
                      <AppRoutes LazyRoute={LazyRoute} />
                    </AppLayout>
                  </AuthenticationLoadingHandler>
                </NotificationProvider>
              </ModalProvider>
            </AuthProvider>
          </ConfigProvider>
        </RouteChangeProvider>
      </Router>
    </QueryClientProvider >
  )
}

export default App
